import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";

type WorkerRecord = {
  id: string;
  fullName: string;
  email: string;
  company?: string | null;
  jobTitle?: string | null;
};

function getWorkerInitials(fullName: string) {
  return fullName
    .trim()
    .split(/\s+/)
    .map((name) => name.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getWorkerCompany(worker: WorkerRecord) {
  return worker.company?.trim() || "No company on file";
}

function getWorkerJobTitle(worker: WorkerRecord) {
  return worker.jobTitle?.trim() || "No job title on file";
}

function WorkerSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div>
      <label htmlFor="worker-search" className="sr-only">
        Search workers
      </label>
      <InputGroup>
        <InputGroupAddon>
          <Search className="size-4" aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          id="worker-search"
          placeholder="Search by name or email"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label="Search workers"
          autoComplete="off"
        />
      </InputGroup>
    </div>
  );
}

function WorkersTable({ workers }: { workers: WorkerRecord[] }) {
  return (
    <div className="table-panel">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-60 px-4">Name</TableHead>
            <TableHead className="min-w-72 px-4">Email</TableHead>
            <TableHead className="min-w-64 px-4">Company</TableHead>
            <TableHead className="min-w-64 px-4">Job title</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {workers.map((worker) => {
            const initials = getWorkerInitials(worker.fullName);
            const company = getWorkerCompany(worker);
            const jobTitle = getWorkerJobTitle(worker);

            return (
              <TableRow key={worker.id} className="align-top">
                <TableCell className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="size-10 shrink-0">
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 space-y-1">
                      <Link
                        to={`/workers/${worker.id}`}
                        className="block truncate font-medium capitalize text-foreground hover:underline"
                      >
                        {worker.fullName}
                      </Link>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                  <span className="block max-w-[18rem] break-all">
                    {worker.email}
                  </span>
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                  {company}
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                  {jobTitle}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function WorkersEmptyState({ isSearching }: { isSearching: boolean }) {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>
          {isSearching ? "No workers match your search" : "No workers found"}
        </EmptyTitle>
        <EmptyDescription>
          {isSearching
            ? "Try a different name or email address, or clear the search to browse the full directory."
            : "Workers will appear here once they have signed in to one of your sites."}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function Workers() {
  const [search, setSearch] = useState("");
  const workers = useQuery(trpc.getWorkers.queryOptions());

  const workerRecords = (workers.data ?? []) as WorkerRecord[];

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const visibleWorkers = query
      ? workerRecords.filter((worker) => {
          const nameMatch = worker.fullName.toLowerCase().includes(query);
          const emailMatch = worker.email.toLowerCase().includes(query);
          return nameMatch || emailMatch;
        })
      : workerRecords;

    return [...visibleWorkers].sort((a, b) =>
      a.fullName.localeCompare(b.fullName, undefined, { sensitivity: "base" })
    );
  }, [workerRecords, search]);

  const isSearching = search.trim().length > 0;

  if (workers.isError) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
        Unable to load the workers list right now.
      </div>
    );
  }

  if (workers.isLoading) {
    return <div className="text-muted-foreground">Loading workers…</div>;
  }

  return (
    <>
      <div className="mb-4">
        <WorkerSearchInput value={search} onChange={setSearch} />
      </div>

      {filtered.length > 0 ? (
        <WorkersTable workers={filtered} />
      ) : (
        <WorkersEmptyState isSearching={isSearching} />
      )}
    </>
  );
}

export default function WorkersIndex() {
  const { pathname } = useLocation();

  if (pathname === "/workers") {
    return (
      <PageContainer className="flex min-h-full flex-col gap-6">
        <PageHeader
          title="Workers"
          description="Browse your workforce, search by name or email, and open worker profiles."
        />
        <Workers />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Outlet />
    </PageContainer>
  );
}
